import React, { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { rozmerLoga, pomerObrazku, TITUL, HLAVICKA } from "../lib/logo";

const STRANA = 794;   // šířka stránky PDF v px

// Výřez stránky PDF ve skutečném měřítku, zmenšený na šířku panelu.
function Vyrez({ vyska, popisek, children, testid }) {
  const ref = useRef(null);
  const [k, setK] = useState(0.5);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setK(Math.min(1, el.clientWidth / STRANA)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="min-w-0">
      <div className="label-up text-muted mb-1">{popisek}</div>
      <div ref={ref} className="rounded-[10px] border border-line bg-white overflow-hidden" style={{ height: vyska * k }} data-testid={testid}>
        <div style={{ width: STRANA, height: vyska, transform: `scale(${k})`, transformOrigin: "0 0" }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Náhled loga v PDF (úvodní strana a hlavička dalších stran) a doladění
 * velikosti. Rozměr se počítá stejně jako v PDF (lib/logo.js ↔ backend/logo.py).
 */
export default function LogoNahled({ logo, nazev, velikost, onVelikost, disabled }) {
  const [pomer, setPomer] = useState(null);
  useEffect(() => {
    let zivy = true;
    if (logo) pomerObrazku(logo).then((p) => zivy && setPomer(p)).catch(() => zivy && setPomer(1.75));
    return () => { zivy = false; };
  }, [logo]);
  if (!logo || !pomer) return null;
  const vel = velikost || 100;
  const t = rozmerLoga(pomer, TITUL, vel), h = rozmerLoga(pomer, HLAVICKA, vel);
  const meta = (m, v) => (
    <div style={{ display: "flex", flexDirection: "column", fontSize: 11, fontWeight: 600 }}>
      <small style={{ fontSize: 8.5, color: "#8d929b", fontWeight: 400 }}>{m}</small>{v}
    </div>
  );
  return (
    <div className="mt-3 rounded-[14px] bg-surface p-3 md:p-4 flex flex-col gap-3" data-testid="logo-nahled">
      <Vyrez vyska={130} popisek="Úvodní strana PDF" testid="logo-nahled-titul">
        <div style={{ padding: "34px 48px", display: "flex", alignItems: "center", gap: 22, fontFamily: "inherit", color: "#14161a" }}>
          <img src={logo} alt={nazev} style={{ width: t.w, height: t.h, objectFit: "contain", objectPosition: "left center" }} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 20, alignItems: "center" }}>
            {meta("Číslo nabídky", "N-2026-001")}{meta("Datum vypracování", "26.10.2026")}{meta("Vypracoval", "Jan Novák")}
            <span style={{ width: 36, height: 36, borderRadius: 99, background: "#eef0f2" }} />
          </div>
        </div>
      </Vyrez>
      <Vyrez vyska={80} popisek="Hlavička dalších stran" testid="logo-nahled-hlavicka">
        <div style={{ padding: "22px 48px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: "1px solid #e6e8eb", fontSize: 9.5, color: "#8d929b" }}>
            <img src={logo} alt={nazev} style={{ width: h.w, height: h.h, objectFit: "contain", objectPosition: "left center" }} />
            <span style={{ margin: "0 auto" }}>Cenová nabídka · tepelné čerpadlo</span>
            <span style={{ display: "flex", flexDirection: "column", textAlign: "right", color: "#14161a", fontWeight: 600 }}>
              <small style={{ fontSize: 8, color: "#8d929b", fontWeight: 400 }}>Číslo nabídky</small>N-2026-001
            </span>
          </div>
        </div>
      </Vyrez>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="t-meta font-700 text-ink w-full md:w-auto">Velikost v PDF</span>
        <input type="range" min={50} max={200} step={10} value={vel} disabled={disabled} aria-label="Velikost loga v PDF"
          onChange={(e) => onVelikost(Number(e.target.value))} className="flex-1 min-w-[140px] accent-ink tap md:min-h-0" data-testid="logo-velikost" />
        <span className="amount t-meta font-700 text-ink w-12 text-right">{vel} %</span>
        {vel !== 100 && !disabled && (
          <button type="button" onClick={() => onVelikost(100)} className="tap md:min-h-0 inline-flex items-center gap-1 t-meta font-700 text-sec hover:text-ink" data-testid="logo-velikost-vychozi">
            <RotateCcw size={13} /> Výchozí
          </button>
        )}
      </div>
      <p className="t-meta text-muted -mt-1">Velikost se nastaví podle tvaru loga (čtvercové vyšší, široké širší). Změnu ulož tlačítkem Uložit firmu.</p>
    </div>
  );
}
